import { inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap, take } from 'rxjs';
import { ApiHttpService, Entity } from 'shared';
import {
  StockSalesReport,
  StockSalesReportArticle,
  StockSalesReportInput,
  StockSalesReportSupplier,
} from './stock-sales-report';

@Injectable({
  providedIn: 'root',
})
export class StockSalesReportService {
  #apiService = inject(ApiHttpService<Entity>);

  /** The suppliers with Stock & Sales Reporting on; none means the client has nothing to report. */
  getSuppliers(): Observable<StockSalesReportSupplier[]> {
    return this.#apiService.getMany('/crown_desk/stock_sales_report_suppliers').pipe(
      take(1),
      map((result) => result.body['hydra:member'] as StockSalesReportSupplier[]),
    );
  }

  /** At most 20 articles of the supplier matching the article number, type number or EAN. */
  searchArticles(supplierId: string, search: string): Observable<StockSalesReportArticle[]> {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.#apiService.getMany(`/crown_desk/stock_sales_report_suppliers/${supplierId}/articles${query}`).pipe(
      take(1),
      map((result) => result.body['hydra:member'] as StockSalesReportArticle[]),
    );
  }

  /**
   * The supplier article with exactly this article number, type number or EAN, ignoring case; null when the supplier
   * has none or more than one.
   */
  findArticle(supplierId: string, number: string): Observable<StockSalesReportArticle | null> {
    const wanted = number.trim().toLowerCase();
    return this.searchArticles(supplierId, number.trim()).pipe(
      map((articles) => {
        const matches = articles.filter((article) =>
          [article.articleNumber, article.typeNumber, article.ean].some((value) => value?.toLowerCase() === wanted),
        );
        return matches.length === 1 ? matches[0] : null;
      }),
    );
  }

  /** The newest report the client sent to the supplier, with its lines; null when there is none. */
  getLastReport(supplierId: string): Observable<StockSalesReport | null> {
    // the api lists the newest first
    return this.#apiService.getMany(`/crown_desk/stock_sales_reports?supplier=${supplierId}&itemsPerPage=1`).pipe(
      take(1),
      map((result) => (result.body['hydra:member'] as StockSalesReport[])[0]),
      switchMap((report) => (report ? this.getReport(`${report.id}`) : of(null))),
    );
  }

  getReport(id: string): Observable<StockSalesReport> {
    return this.#apiService.get(`/crown_desk/stock_sales_reports/${id}`).pipe(
      take(1),
      map((result) => result.body as StockSalesReport),
    );
  }

  /** Mails the report to the supplier, with a copy to the employee. */
  send(report: StockSalesReportInput): Observable<StockSalesReport> {
    return this.#apiService.post('/crown_desk/stock_sales_reports', report as Entity).pipe(
      take(1),
      map((result) => result.body as StockSalesReport),
    );
  }
}
